import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, createInventoryCategory } from '../core/_requests'
import { CompanyGroupAdmin } from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryCrudAccess } from '../core/libraryPermissionAccess'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface CategoryModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vesselId?: number
  embedded?: boolean
  stayOpenOnSubmit?: boolean
}

const CategoryModal: FC<CategoryModalProps> = ({
  visible,
  onClose,
  onSubmit,
  embedded = false,
  stayOpenOnSubmit = false,
}) => {
  const { currentUser } = useAuth()
  const { can, getScope } = useProcurementPermissions()
  const canCreate = libraryCrudAccess.category.create(can)
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.CREATE_LIBRARY_CATEGORY,
    PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({ name: '', company: '' })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    }
  }, [visible, libraryScopeAll])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!formData.name.trim()) newErrors.name = 'Category Name is required'
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetFormState = () => {
    setFormData({ name: '', company: '' })
    setErrors({})
    setCompanies([])
    setIsSubmitting(false)
  }

  const handleClose = () => {
    resetFormState()
    onClose()
  }

  const handleSubmit = async () => {
    if (!canCreate) {
      toast.error('You do not have permission to create categories.')
      return
    }
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      let cgaIdToUse: number
      if (libraryScopeAll) {
        cgaIdToUse = parseInt(formData.company, 10)
      } else if (libraryScopeCompany && effectiveCgaId) {
        cgaIdToUse = effectiveCgaId
      } else {
        const tid = tenantCgaId
        if (!tid) {
          toast.error('Unable to resolve company scope for this user')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = tid
      }

      await createInventoryCategory({
        name: formData.name,
        cgaid: { id: cgaIdToUse },
        vesselId: undefined,
        vesselType: undefined,
      })
      toast.success('Category added successfully!')
      onSubmit()
      if (stayOpenOnSubmit) {
        resetFormState()
      } else {
        handleClose()
      }
    } catch (error: any) {
      console.error('Error adding category:', error)
      toast.error('Failed to add category')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!visible || !canCreate) return null

  const modalContent = (
    <div
      className='modal-content bg-white'
      style={{
        color: '#181C32',
        height: embedded ? '100%' : undefined,
        borderRadius: embedded ? '12px' : undefined,
        boxShadow: embedded ? '0 12px 40px rgba(15,23,42,0.06)' : undefined,
        border: embedded ? '1px solid #E9EEF5' : undefined,
      }}
    >
      <div className='modal-header'>
        <h5 className='modal-title'>Add Category</h5>
        <button type='button' className='btn-close' onClick={handleClose}></button>
      </div>
      <div className='modal-body'>
        <div className='row g-3'>
          {libraryScopeAll && (
            <div className='col-md-6'>
              <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                Company
              </label>
              <SearchableSelect
                className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                name='company'
                value={formData.company}
                onChange={handleInputChange}
                disabled={isLoadingDropdowns}
              >
                <option value=''>Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </SearchableSelect>
              {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
            </div>
          )}

          <div className='col-12'>
            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
              Category Name
            </label>
            <input
              type='text'
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              placeholder='Enter category name'
              style={{ color: '#000' }}
            />
            {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
          </div>

          {libraryScopeCompany && effectiveCgaId && (
            <div className='col-12'>
              <div className='alert alert-info'>
                <i className='fas fa-info-circle me-2'></i>
                Category will be created company-wide under your company group (ID: {effectiveCgaId}).
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </button>
        <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Category'}
        </button>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div
        className='h-100 d-flex flex-column'
        style={{ background: '#F8FAFC', padding: '12px', overflow: 'auto' }}
      >
        {modalContent}
      </div>
    )
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        {modalContent}
      </div>
    </div>
  )
}

export default CategoryModal
